import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  X,
  Search,
  ImageIcon,
  Sparkles,
  Download,
  Video,
  Music,
  Play,
  Pause,
  Wand2,
  RefreshCw,
  Loader2,
  Globe,
} from "lucide-react";

import Masonry from "react-masonry-css";

import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useFeatures } from "@/hooks/useFeatures";
import { useImageGeneration } from "@/components/editor/image-generate-panel/use-image-generation";
import { useMediaLibrary } from "@/components/editor/media-library-panel/use-media-library";
import { useInfiniteScroll } from "@/components/editor/media-library-panel/use-infinite-scroll";
import { ImageCard } from "@/components/editor/media-library-panel/images-list";
import { VideoCard } from "@/components/editor/media-library-panel/videos-list";
import { SoundsList } from "@/components/editor/media-library-panel/sounds-list";
import type { ImageGenRequestItem } from "@/types/image-generate-panel.types";
import type {
  LocalMediaAsset,
  MediaLibraryResult,
} from "@/types/media-library-panel.types";
import {
  ASPECT_RATIOS,
  STYLE_PRESETS,
} from "@/types/image-generate-panel.types";

type PickerMediaType = "image" | "video" | "audio";

/** Compact list for local audio assets with inline playback preview */
function AudioLocalAssetsList({
  items,
  onItemClick,
}: {
  items: (ImageGenRequestItem | LocalMediaAsset)[];
  onItemClick: (item: ImageGenRequestItem | LocalMediaAsset) => void;
}): React.ReactElement {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.ontimeupdate = null;
      audioRef.current = null;
    }
    setPlayingId(null);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  const handlePlayPause = useCallback(
    (e: React.MouseEvent, id: string, src: string) => {
      e.stopPropagation();
      if (playingId === id) {
        stopAudio();
        return;
      }
      stopAudio();
      const audio = new Audio(src);
      audio.ontimeupdate = () => setCurrentTime(audio.currentTime);
      audio.onloadedmetadata = () => setDuration(audio.duration);
      audio.onended = () => stopAudio();
      audio.onerror = () => stopAudio();
      audio.play().catch(() => stopAudio());
      audioRef.current = audio;
      setPlayingId(id);
    },
    [playingId, stopAudio],
  );

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const formatTime = (sec: number): string => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className="flex flex-col gap-0.5">
      {items.map((item) => {
        const label =
          "expectedFilename" in item
            ? (item as ImageGenRequestItem).expectedFilename
            : (item as LocalMediaAsset).name;
        const src =
          ("downloadUrl" in item && item.downloadUrl) ||
          ("previewUrl" in item && (item as LocalMediaAsset).previewUrl) ||
          ("assetPath" in item && item.assetPath) ||
          "";
        const isPlaying = playingId === item.id;
        const progress =
          isPlaying && duration > 0 ? (currentTime / duration) * 100 : 0;

        return (
          <div
            key={item.id}
            onClick={() => onItemClick(item)}
            className={cn(
              "group relative flex flex-col transition-all duration-200 cursor-pointer rounded-lg",
              isPlaying ? "bg-purple-500/5" : "hover:bg-muted/60",
            )}
          >
            <div className="flex items-center gap-3 px-3 py-2 min-w-0">
              <button
                onClick={(e) => handlePlayPause(e, item.id, src as string)}
                className={cn(
                  "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all",
                  isPlaying
                    ? "bg-purple-500/15 text-purple-500"
                    : "bg-muted/80 text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                {isPlaying ? (
                  <Pause className="w-3.5 h-3.5" />
                ) : (
                  <Play className="w-3.5 h-3.5 ml-0.5" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate" title={label}>
                  {label}
                </p>
                {isPlaying && (
                  <span className="text-[10px] text-purple-500 tabular-nums">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                )}
              </div>
              <Download className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            {isPlaying && (
              <div className="h-[2px] bg-purple-500/10 mx-3 mb-1.5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-500 rounded-full transition-[width] duration-300 ease-linear"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface MediaPickerPopupProps {
  open: boolean;
  mediaType?: PickerMediaType;
  onSelect: (src: string, alt: string) => void;
  onSkip: () => void;
  onClose: () => void;
  projectId?: string;
  devContainerId?: string;
  previewUrl?: string;
}

type Tab = "ai-generated" | "media-library";

export function MediaPickerPopup({
  open,
  mediaType = "image",
  onSelect,
  onSkip,
  onClose,
  projectId,
  devContainerId,
  previewUrl,
}: MediaPickerPopupProps) {
  const showAiTab = mediaType === "image"; // AI generation only for images, not video/audio
  const [activeTab, setActiveTab] = useState<Tab>(
    showAiTab ? "ai-generated" : "media-library",
  );
  const [search, setSearch] = useState("");
  const [showGenerateForm, setShowGenerateForm] = useState(false);

  const { features } = useFeatures();
  const enabledProviderNames = features.mediaLibraryProviders ?? [
    "pexels",
    "unsplash",
    "freesound",
  ];
  const providerLabel = enabledProviderNames
    .map((p: string) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(", ");

  // Use hook's state directly for the generate form (same pattern as ImageGeneratePanel)
  // This avoids the broken local-state-to-hook-state sync issue
  const {
    generatedImages,
    isLoadingAssets: isLoadingGenerated,
    loadGeneratedImages,
    isGenerating,
    handleGenerate,
    prompt,
    setPrompt,
    aspectRatio,
    setAspectRatio,
    stylePreset,
    setStylePreset,
    generatingImages,
    removeGeneratingImage,
  } = useImageGeneration({ projectId, devContainerId, previewUrl });

  const {
    localAssets,
    isLoadingAssets: isLoadingMedia,
    loadLocalAssets,
    // Provider search
    query: mediaQuery,
    setQuery: setMediaQuery,
    setMediaType: setMediaSearchType,
    searchResults,
    isSearching,
    handleSearch: mediaHandleSearch,
    isShowingCurated,
    // Infinite scroll
    hasMore,
    isLoadingMore,
    loadMore,
    // Download
    handleDownload: mediaHandleDownload,
    downloadingIds,
    downloadingItems,
  } = useMediaLibrary({ devContainerId, previewUrl });

  const showProviderResults =
    activeTab === "media-library" && search.trim().length > 0;

  // Infinite scroll sentinel for provider search results
  const sentinelRef = useInfiniteScroll({
    onLoadMore: loadMore,
    hasMore: showProviderResults ? hasMore : false,
    isLoading: isSearching || isLoadingMore,
    threshold: 200,
  });

  // Track which item is being downloaded + selected after download
  const pendingSelectRef = useRef<{ title: string } | null>(null);

  // Set the media search type to match the picker's mediaType
  useEffect(() => {
    setMediaSearchType(mediaType);
  }, [mediaType, setMediaSearchType]);

  // Load assets on mount
  useEffect(() => {
    if (open) {
      loadGeneratedImages();
      loadLocalAssets();
    }
  }, [open, loadGeneratedImages, loadLocalAssets]);

  // When local assets update after download, auto-select the pending item
  useEffect(() => {
    if (!pendingSelectRef.current) return;
    const { title } = pendingSelectRef.current;
    const slug = title
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, "-")
      .replace(/-{2,}/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 60);
    const match = localAssets.find((a) => a.name.includes(slug));
    if (match) {
      pendingSelectRef.current = null;
      onSelect(match.assetPath, match.name);
    }
  }, [localAssets, onSelect]);

  // Debounced provider search when typing in media-library tab
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (activeTab !== "media-library") return;
    clearTimeout(searchTimeoutRef.current);

    if (search.trim()) {
      setMediaQuery(search.trim());
      searchTimeoutRef.current = setTimeout(() => {
        mediaHandleSearch();
      }, 400);
    }

    return () => clearTimeout(searchTimeoutRef.current);
  }, [search, activeTab, setMediaQuery, mediaHandleSearch]);

  // Filter to completed AI images with a download URL
  const completedImages = useMemo(
    () =>
      generatedImages.filter(
        (img) =>
          img.status === "completed" && (img.downloadUrl || img.assetPath),
      ),
    [generatedImages],
  );

  // Filter media library to matching type
  const filteredAssets = useMemo(
    () => localAssets.filter((asset) => asset.type === mediaType),
    [localAssets, mediaType],
  );

  // Apply search filter for AI generated tab
  const filteredGenerated = useMemo(() => {
    if (!search.trim()) return completedImages;
    const q = search.toLowerCase();
    return completedImages.filter(
      (img) =>
        img.prompt.toLowerCase().includes(q) ||
        img.expectedFilename.toLowerCase().includes(q),
    );
  }, [completedImages, search]);

  // For media library tab: show provider results when searching, local assets when not
  const filteredMedia = useMemo(() => {
    if (!search.trim()) return filteredAssets;
    const q = search.toLowerCase();
    return filteredAssets.filter((asset) =>
      asset.name.toLowerCase().includes(q),
    );
  }, [filteredAssets, search]);

  const isLoading =
    activeTab === "ai-generated"
      ? isLoadingGenerated
      : showProviderResults
        ? isSearching
        : isLoadingMedia;
  const localItems: (ImageGenRequestItem | LocalMediaAsset)[] =
    activeTab === "ai-generated" ? filteredGenerated : filteredMedia;
  const isEmpty =
    !isLoading &&
    (showProviderResults
      ? searchResults.length === 0
      : localItems.length === 0) &&
    generatingImages.length === 0 &&
    downloadingItems.filter((d) => d.status === "downloading").length === 0;

  // Handle click on a provider search result: download then auto-select
  const handleProviderItemClick = useCallback(
    async (item: MediaLibraryResult) => {
      const key = `${item.provider}-${item.id}`;
      if (downloadingIds.has(key)) return; // already downloading
      pendingSelectRef.current = { title: item.title };
      await mediaHandleDownload([item]);
    },
    [downloadingIds, mediaHandleDownload],
  );

  if (!open) return null;

  const handleMediaClick = (item: ImageGenRequestItem | LocalMediaAsset) => {
    if ("assetPath" in item && item.assetPath) {
      const alt =
        "prompt" in item
          ? (item as ImageGenRequestItem).expectedFilename
          : (item as LocalMediaAsset).name;
      onSelect(item.assetPath, alt);
    } else if ("downloadUrl" in item && item.downloadUrl) {
      const genItem = item as ImageGenRequestItem;
      onSelect(genItem.downloadUrl!, genItem.expectedFilename);
    } else if ("previewUrl" in item && item.previewUrl) {
      const mediaItem = item as LocalMediaAsset;
      onSelect(mediaItem.assetPath || mediaItem.previewUrl!, mediaItem.name);
    }
  };

  const getPreviewSrc = (item: ImageGenRequestItem | LocalMediaAsset) => {
    if ("downloadUrl" in item && item.downloadUrl) return item.downloadUrl;
    if ("previewUrl" in item && item.previewUrl) return item.previewUrl;
    return "";
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-background border border-border rounded-xl shadow-2xl w-[520px] max-h-[600px] flex flex-col animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h3 className="text-sm font-semibold text-foreground">
            {mediaType === "video"
              ? "Choose a Video"
              : mediaType === "audio"
                ? "Choose Audio"
                : "Choose Media"}
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 pb-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="text"
              placeholder={
                activeTab === "media-library"
                  ? `Search stock ${mediaType === "video" ? "videos" : mediaType === "audio" ? "audio" : "photos"}${providerLabel ? ` (${providerLabel})` : ""}...`
                  : mediaType === "video"
                    ? "Search videos..."
                    : mediaType === "audio"
                      ? "Search audio..."
                      : "Search images..."
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              autoFocus
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-4 pb-2">
          {showAiTab && (
            <button
              onClick={() => setActiveTab("ai-generated")}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                activeTab === "ai-generated"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground",
              )}
            >
              <Sparkles className="w-3 h-3" />
              AI Generated
              {completedImages.length > 0 && (
                <span className="ml-0.5 text-[10px] opacity-70">
                  ({completedImages.length})
                </span>
              )}
            </button>
          )}
          <button
            onClick={() => setActiveTab("media-library")}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
              activeTab === "media-library"
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground",
            )}
          >
            <Download className="w-3 h-3" />
            Media Library
            {filteredAssets.length > 0 && (
              <span className="ml-0.5 text-[10px] opacity-70">
                ({filteredAssets.length})
              </span>
            )}
          </button>
          <button
            onClick={() => {
              loadGeneratedImages();
              loadLocalAssets();
            }}
            className="ml-auto p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title="Refresh"
          >
            <RefreshCw
              className={cn("w-3.5 h-3.5", isLoading && "animate-spin")}
            />
          </button>
        </div>

        {/* AI Generate Inline Form - only in AI tab for images */}
        {activeTab === "ai-generated" && showAiTab && (
          <div className="px-4 pb-2">
            {!showGenerateForm ? (
              <button
                onClick={() => setShowGenerateForm(true)}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-dashed border-purple-400/40 text-purple-600 dark:text-purple-400 hover:bg-purple-500/5 hover:border-purple-400/60 transition-colors"
              >
                <Wand2 className="w-3.5 h-3.5" />
                Generate New Image with AI
              </button>
            ) : (
              <div className="space-y-2 p-3 rounded-lg border border-purple-500/20 bg-purple-500/5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-purple-600 dark:text-purple-400 flex items-center gap-1">
                    <Wand2 className="w-3 h-3" />
                    AI Image Generator
                  </span>
                  <button
                    onClick={() => setShowGenerateForm(false)}
                    className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <textarea
                  placeholder="Describe the image you want to create..."
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="w-full min-h-[48px] max-h-[80px] resize-none text-xs rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-purple-500/50 p-2"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      handleGenerate();
                    }
                  }}
                />
                <div className="flex items-center gap-1.5">
                  <select
                    value={aspectRatio}
                    onChange={(e) => setAspectRatio(e.target.value)}
                    className="h-7 text-xs rounded-md border border-border bg-background text-foreground px-1.5 focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    {ASPECT_RATIOS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={stylePreset}
                    onChange={(e) => setStylePreset(e.target.value)}
                    className="h-7 text-xs rounded-md border border-border bg-background text-foreground px-1.5 focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    {STYLE_PRESETS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating || !prompt.trim()}
                    className={cn(
                      "ml-auto flex items-center gap-1 px-3 h-7 text-xs font-medium rounded-md transition-all",
                      "bg-purple-600 hover:bg-purple-700 text-white",
                      "disabled:opacity-50 disabled:cursor-not-allowed",
                    )}
                  >
                    {isGenerating ? (
                      <>
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-3 h-3" />
                        Generate
                      </>
                    )}
                  </button>
                </div>
                <p className="text-[10px] text-muted-foreground/60">
                  Press Cmd/Ctrl + Enter to generate
                </p>
              </div>
            )}
          </div>
        )}

        {/* Provider search hint */}
        {activeTab === "media-library" &&
          showProviderResults &&
          !isSearching &&
          searchResults.length > 0 && (
            <div className="px-4 pb-1.5">
              <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Globe className="w-3 h-3" />
                Stock results for &ldquo;{search.trim()}&rdquo; &mdash; click to
                download &amp; use
              </p>
            </div>
          )}

        {/* Media Grid */}
        <ScrollArea className="flex-1 min-h-0 overflow-hidden px-4 overflow-y-auto">
          {/* Generating images progress */}
          {activeTab === "ai-generated" && generatingImages.length > 0 && (
            <div className="grid grid-cols-3 gap-2 pb-2">
              {generatingImages.map((genImg) => (
                <div
                  key={genImg.requestId}
                  className={cn(
                    "relative aspect-video rounded-lg overflow-hidden flex flex-col items-center justify-center",
                    genImg.status === "failed"
                      ? "border border-red-500/30 bg-red-500/5"
                      : "border border-purple-500/30 bg-purple-500/5",
                  )}
                >
                  {genImg.status === "failed" ? (
                    <>
                      <X className="w-4 h-4 text-red-500 mb-0.5" />
                      <p className="text-[9px] text-red-500 font-medium">
                        Failed
                      </p>
                      <button
                        onClick={() => removeGeneratingImage(genImg.requestId)}
                        className="mt-1 text-[9px] text-red-400 hover:text-red-500 underline transition-colors"
                      >
                        Dismiss
                      </button>
                    </>
                  ) : (
                    <>
                      <Loader2 className="w-5 h-5 text-purple-500 animate-spin mb-1" />
                      <p className="text-[9px] text-purple-500/70 truncate max-w-full px-2">
                        {genImg.prompt.slice(0, 30)}
                        {genImg.prompt.length > 30 ? "..." : ""}
                      </p>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {isLoading ? (
            <Masonry
              breakpointCols={3}
              className="flex -ml-2 w-auto"
              columnClassName="pl-2 bg-clip-padding"
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton
                  key={i}
                  className="relative overflow-hidden rounded-lg mb-2"
                  style={{
                    aspectRatio:
                      mediaType === "video"
                        ? i % 3 === 0
                          ? "9/16"
                          : "16/9"
                        : i % 2 === 0
                          ? "3/4"
                          : "4/3",
                  }}
                />
              ))}
            </Masonry>
          ) : isEmpty ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              {mediaType === "video" ? (
                <Video className="w-10 h-10 mb-3 opacity-40" />
              ) : mediaType === "audio" ? (
                <Music className="w-10 h-10 mb-3 opacity-40" />
              ) : (
                <ImageIcon className="w-10 h-10 mb-3 opacity-40" />
              )}
              <p className="text-xs font-medium">
                {mediaType === "video"
                  ? "No videos found"
                  : mediaType === "audio"
                    ? "No audio found"
                    : "No images found"}
              </p>
              <p className="text-[11px] mt-1 opacity-70">
                {activeTab === "ai-generated"
                  ? "Generate images using the form above or the AI Image panel"
                  : showProviderResults
                    ? `No stock ${mediaType === "video" ? "videos" : mediaType === "audio" ? "audio" : "photos"} found for "${search.trim()}"`
                    : `Type to search stock ${mediaType === "video" ? "videos" : mediaType === "audio" ? "audio" : "photos"}, or download from the Media Library panel`}
              </p>
            </div>
          ) : showProviderResults ? (
            /* Provider search results */
            mediaType === "audio" ? (
              /* Audio: playlist-style list with preview playback */
              <SoundsList
                results={searchResults}
                onDownload={handleProviderItemClick}
                downloadingIds={downloadingIds}
                isLoading={isSearching}
                hasMore={hasMore}
                isLoadingMore={isLoadingMore}
                onLoadMore={loadMore}
              />
            ) : (
              /* Image/Video: masonry grid */
              <>
                <Masonry
                  breakpointCols={3}
                  className="flex -ml-2 w-auto"
                  columnClassName="pl-2 bg-clip-padding"
                >
                  {searchResults.map((item) => {
                    const cardId = `${item.provider}-${item.id}`;
                    const isDownloading = downloadingIds.has(cardId);

                    if (mediaType === "video") {
                      return (
                        <VideoCard
                          key={cardId}
                          result={item}
                          onDownload={() => handleProviderItemClick(item)}
                          isDownloading={isDownloading}
                        />
                      );
                    }

                    return (
                      <ImageCard
                        key={cardId}
                        result={item}
                        onDownload={handleProviderItemClick}
                        isDownloading={isDownloading}
                      />
                    );
                  })}
                  {/* Loading more skeletons */}
                  {isLoadingMore &&
                    Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton
                        key={`loading-${i}`}
                        className="relative overflow-hidden rounded-lg mb-2"
                        style={{
                          aspectRatio: mediaType === "video" ? "16/9" : "4/3",
                        }}
                      />
                    ))}
                </Masonry>
                {/* Infinite scroll sentinel */}
                <div ref={sentinelRef} className="h-px" />
              </>
            )
          ) : mediaType === "audio" ? (
            /* Local audio assets — list layout with playback preview */
            <AudioLocalAssetsList
              items={localItems}
              onItemClick={handleMediaClick}
            />
          ) : (
            /* Local image/video assets — masonry layout */
            <Masonry
              breakpointCols={3}
              className="flex -ml-2 w-auto"
              columnClassName="pl-2 bg-clip-padding"
            >
              {localItems.map((item) => {
                const src = getPreviewSrc(item);
                const label =
                  "expectedFilename" in item
                    ? (item as ImageGenRequestItem).expectedFilename
                    : (item as LocalMediaAsset).name;

                return (
                  <div
                    key={item.id}
                    onClick={() => handleMediaClick(item)}
                    className="group relative mb-2 rounded-lg overflow-hidden border border-border hover:border-primary hover:ring-1 hover:ring-primary/50 transition-all bg-muted cursor-pointer w-full"
                    title={label}
                  >
                    {src ? (
                      mediaType === "video" ? (
                        <video
                          src={src}
                          className="w-full h-auto block transition-transform group-hover:scale-105"
                          muted
                          preload="metadata"
                        />
                      ) : (
                        <img
                          src={src}
                          alt={label}
                          className="w-full h-auto block transition-transform group-hover:scale-105"
                          loading="lazy"
                        />
                      )
                    ) : (
                      <div className="w-full aspect-video flex items-center justify-center">
                        {mediaType === "video" ? (
                          <Video className="w-6 h-6 text-muted-foreground/50" />
                        ) : (
                          <ImageIcon className="w-6 h-6 text-muted-foreground/50" />
                        )}
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-[10px] text-white truncate">{label}</p>
                    </div>
                  </div>
                );
              })}
            </Masonry>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSkip}
            className="px-3 py-1.5 text-xs font-medium bg-muted hover:bg-muted/80 text-foreground rounded-md transition-colors"
          >
            Skip (Use Placeholder)
          </button>
        </div>
      </div>
    </div>
  );
}

/** @deprecated Use MediaPickerPopup instead */
export const ImagePickerPopup = MediaPickerPopup;
